import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { callClaude } from '../claude'

function BidHealth({ opportunity, profile, onClose, onHealthSaved }) {
  const [health, setHealth] = useState(opportunity.bid_health || null)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)
  const [bidData, setBidData] = useState(null)
  const [loading, setLoading] = useState(true)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBidData() }, [])

  const loadBidData = async () => {
    setLoading(true)
    const oppId = opportunity.id

    const [
      { data: bidPlan },
      { data: milestones },
      { data: actions },
      { data: questions },
      { data: cqs },
    ] = await Promise.all([
      supabase.from('bid_plans').select('id, status').eq('opportunity_id', oppId).single(),
      supabase.from('milestones').select('id, title, due_date, completed').eq('opportunity_id', oppId),
      supabase.from('actions').select('id, title, due_date, status, assigned_to').eq('opportunity_id', oppId),
      supabase.from('questions').select('id, status').eq('opportunity_id', oppId),
      supabase.from('clarification_questions').select('id, status, customer_response').eq('opportunity_id', oppId),
    ])

    const now = new Date()
    const deadline = opportunity.deadline ? new Date(opportunity.deadline) : null
    const daysToDeadline = deadline ? Math.ceil((deadline - now) / 86400000) : null

    const overdueMillestones = (milestones || []).filter(m => !m.completed && m.due_date && new Date(m.due_date) < now)
    const completedMilestones = (milestones || []).filter(m => m.completed)
    const overdueActions = (actions || []).filter(a => a.status !== 'complete' && a.due_date && new Date(a.due_date) < now)
    const unassignedActions = (actions || []).filter(a => !a.assigned_to && a.status !== 'complete')
    const unansweredQuestions = (questions || []).filter(q => q.status !== 'answered' && q.status !== 'complete')
    const pendingCQs = (cqs || []).filter(c => !c.customer_response)

    setBidData({
      bidPlan,
      totalMilestones: (milestones || []).length,
      completedMilestones: completedMilestones.length,
      overdueMillestones: overdueMillestones.length,
      overdueMillestonesList: overdueMillestones,
      totalActions: (actions || []).length,
      overdueActions: overdueActions.length,
      unassignedActions: unassignedActions.length,
      totalQuestions: (questions || []).length,
      unansweredQuestions: unansweredQuestions.length,
      totalCQs: (cqs || []).length,
      pendingCQs: pendingCQs.length,
      daysToDeadline,
    })
    setLoading(false)
  }

  const handleGenerate = async () => {
    if (!bidData) return
    setGenerating(true); setError(null)
    try {
      const system = 'You are an expert bid manager assessing the health of a live bid. Be direct, specific and commercially focused. Identify genuine risks not generic ones. Respond in JSON only.'

      const d = bidData
      const prompt = `Assess the health of this live bid.

Opportunity: ${opportunity.title}
Buyer: ${opportunity.buyer}
${d.daysToDeadline !== null ? 'Days to Deadline: ' + d.daysToDeadline : 'No deadline set'}

Bid Plan: ${d.bidPlan ? 'Exists' : 'Not created'}
Milestones: ${d.completedMilestones}/${d.totalMilestones} complete, ${d.overdueMillestones} overdue
${d.overdueMillestonesList?.length ? 'Overdue: ' + d.overdueMillestonesList.map(m => m.title).join(', ') : ''}
Actions: ${d.totalActions} total, ${d.overdueActions} overdue, ${d.unassignedActions} unassigned
Response Questions: ${d.totalQuestions - d.unansweredQuestions}/${d.totalQuestions} answered
Clarification Questions: ${d.totalCQs - d.pendingCQs}/${d.totalCQs} have customer responses

Respond with JSON:
{
  "status": "On Track" | "At Risk" | "Critical",
  "summary": "One sentence overall health assessment",
  "issues": [
    { "severity": "High" | "Medium" | "Low", "issue": "specific issue description" }
  ],
  "recommended_actions": ["action 1", "action 2", "action 3"]
}`

      const result = await callClaude({ system, prompt, max_tokens: 600 })
      const clean = result.replace(/```json/g, '').replace(/```/g, '').trim()
      const match = clean.match(/\{[\s\S]*\}/)
      if (!match) throw new Error('No JSON in response')
      const parsed = JSON.parse(match[0])

      await supabase
        .from('opportunities')
        .update({ bid_health: { ...parsed, generated_at: new Date().toISOString() } })
        .eq('id', opportunity.id)

      setHealth(parsed)
      if (onHealthSaved) onHealthSaved({ ...parsed, generated_at: new Date().toISOString() })
    } catch (err) {
      console.error('Bid health error:', err)
      setError('Could not generate health assessment. Please try again.')
    }
    setGenerating(false)
  }

  const statusColour = (status) => {
    if (status === 'Critical') return 'var(--red)'
    if (status === 'At Risk') return '#E67E22'
    return 'var(--green)'
  }

  const severityColour = (severity) => {
    if (severity === 'High') return 'var(--red)'
    if (severity === 'Medium') return '#E67E22'
    return 'var(--green)'
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: 'var(--white)', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ background: 'var(--green-dark)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '4px' }}>Bid Health Monitor</div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.375rem', fontWeight: 600, color: 'var(--white)' }}>{opportunity.title}</div>
            <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '2px' }}>{opportunity.buyer}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--silver)', fontSize: '1.5rem', cursor: 'pointer', padding: '0', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '24px' }}>
          {error && <div className="error-msg">{error}</div>}

          {loading && <p style={{ color: 'var(--slate)' }}>Loading bid data...</p>}

          {!loading && bidData && (
            <div>
              {/* Data summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
                {[
                  { label: 'Milestones', value: bidData.completedMilestones + '/' + bidData.totalMilestones, alert: bidData.overdueMillestones > 0, alertText: bidData.overdueMillestones + ' overdue' },
                  { label: 'Actions', value: (bidData.totalActions - bidData.overdueActions) + '/' + bidData.totalActions, alert: bidData.overdueActions > 0 || bidData.unassignedActions > 0, alertText: bidData.overdueActions + ' overdue' },
                  { label: 'Questions', value: (bidData.totalQuestions - bidData.unansweredQuestions) + '/' + bidData.totalQuestions, alert: bidData.unansweredQuestions > 0, alertText: bidData.unansweredQuestions + ' unanswered' },
                  { label: 'CQ Responses', value: (bidData.totalCQs - bidData.pendingCQs) + '/' + bidData.totalCQs, alert: bidData.pendingCQs > 0, alertText: bidData.pendingCQs + ' pending' },
                  { label: 'Days to Deadline', value: bidData.daysToDeadline !== null ? bidData.daysToDeadline : '—', alert: bidData.daysToDeadline !== null && bidData.daysToDeadline < 14, alertText: 'Urgent' },
                  { label: 'Bid Plan', value: bidData.bidPlan ? 'Created' : 'Missing', alert: !bidData.bidPlan, alertText: 'Not created' },
                ].map((item, i) => (
                  <div key={i} style={{ background: item.alert ? '#FFF5F5' : 'var(--off-white)', border: '1px solid ' + (item.alert ? '#FCCACA' : 'var(--silver)'), padding: '10px 12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: item.alert ? 'var(--red)' : 'var(--green-dark)' }}>{item.value}</div>
                    {item.alert && <div style={{ fontSize: '0.6875rem', color: 'var(--red)', marginTop: '2px' }}>{item.alertText}</div>}
                  </div>
                ))}
              </div>

              {/* AI Assessment */}
              {health && (
                <div style={{ background: 'var(--green-dark)', padding: '16px 20px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>AI Assessment</div>
                    <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 10px', background: statusColour(health.status), color: 'var(--white)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {health.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9375rem', color: 'var(--white)', lineHeight: 1.5, marginBottom: '12px', fontWeight: 500 }}>{health.summary}</div>
                  {health.issues && health.issues.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Flagged Issues</div>
                      {health.issues.map((issue, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <span style={{ fontSize: '0.625rem', fontWeight: 700, color: severityColour(issue.severity), flexShrink: 0, paddingTop: '2px', textTransform: 'uppercase' }}>{issue.severity}</span>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--white)', lineHeight: 1.4 }}>{issue.issue}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {health.recommended_actions && health.recommended_actions.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Recommended Actions</div>
                      {health.recommended_actions.map((action, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '0.8125rem', color: 'var(--white)' }}>
                          <span style={{ color: 'var(--green-light)', flexShrink: 0 }}>{i + 1}.</span>{action}
                        </div>
                      ))}
                    </div>
                  )}
                  {health.generated_at && (
                    <div style={{ fontSize: '0.6875rem', color: 'var(--silver)', marginTop: '12px', fontStyle: 'italic' }}>
                      Generated {new Date(health.generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                {profile?.organisations?.ai_enabled && (
                  <button className="btn btn--primary" onClick={handleGenerate} disabled={generating} style={{ flex: 1 }}>
                    {generating ? '⏳ Assessing...' : health ? '↻ Re-assess Bid Health' : '✦ Assess Bid Health'}
                  </button>
                )}
                <button className="btn btn--secondary" onClick={onClose}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BidHealth
